import {
  useCallback,
  useId,
  useLayoutEffect,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { cn } from './cn.js';
import { placeSpotlight, type SpotlightPlacement } from './spotlightPlacement.js';
import { useDialogBehavior } from './useDialogBehavior.js';

export interface SpotlightProps {
  /**
   * The element to light up and point at. Null centres the popover over a fully dimmed page
   * instead — so a target that is missing (not rendered, renamed) degrades to a plain dialog
   * rather than breaking whatever uses this.
   */
  target: HTMLElement | null;
  /** Heading text. Also names the dialog for assistive tech, so it is required. */
  title: string;
  /** Escape. There is no backdrop to click: a stray tap on the dark area does nothing. */
  onClose: () => void;
  /** Changing this re-places focus inside the popover — pass the step, for a tour. */
  focusKey?: string | number;
  /** The control to focus on open and on every `focusKey` change, instead of the first one. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Height of anything fixed across the top of the page (a sticky header). A target under it is
   * scrolled clear first, since the overlay would otherwise light up the header instead.
   */
  insetTop?: number;
  children: ReactNode;
}

/** Space between the target and the edge of the hole. */
const PADDING = 6;
/** Space between the hole and the popover — the arrow sits in it. */
const GAP = 12;
/** The page's own side gutter: the popover never comes closer to an edge than this. */
const MARGIN = 16;

/**
 * A small popover pointing at one element, with the rest of the page dimmed around it — the
 * building block of a guided tour (MYP-83).
 *
 * The dim layer is a box over the target with a huge shadow, rather than an SVG mask: the shadow
 * darkens everything outside the box and the box itself stays clear, and moving it is moving one
 * box. A separate transparent layer under the popover takes every click, so the page cannot be used
 * while the tour talks about it.
 *
 * It is a modal dialog in every other respect, and shares `Modal`'s plumbing (`useDialogBehavior`):
 * focus trapped in the popover and returned to the opener after, Escape, page scroll locked.
 * Programmatic scrolling still works under the lock, which is how a target is brought into view.
 *
 * It sits one layer above `Modal` and `Sheet` (z-60 against their z-50), so a tour can point into
 * an open panel.
 *
 * The hole and the popover follow the target through scrolling and resizing. They glide between
 * targets, except for people who have asked their device for less motion.
 */
export function Spotlight({
  target,
  title,
  onClose,
  focusKey,
  initialFocusRef,
  insetTop = 0,
  children,
}: SpotlightProps) {
  const titleId = useId();
  const { panelRef } = useDialogBehavior<HTMLDivElement>({
    dismissible: true,
    onClose,
    focusKey,
    initialFocusRef,
  });
  const [placement, setPlacement] = useState<SpotlightPlacement | null>(null);

  const measure = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = target?.getBoundingClientRect();
    setPlacement(
      placeSpotlight({
        target: rect
          ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
          : undefined,
        popover: { width: panel.offsetWidth, height: panel.offsetHeight },
        // clientWidth leaves out a desktop scrollbar, which the popover must not sit under.
        viewport: { width: document.documentElement.clientWidth, height: window.innerHeight },
        padding: PADDING,
        gap: GAP,
        margin: MARGIN,
      }),
    );
  }, [panelRef, target]);

  // A target out of view — or under the sticky header — is brought to the middle first.
  useLayoutEffect(() => {
    if (!target) return;
    const rect = target.getBoundingClientRect();
    if (rect.top < insetTop + PADDING || rect.bottom > window.innerHeight - PADDING) {
      target.scrollIntoView({ block: 'center' });
    }
  }, [target, insetTop]);

  // Measured before paint, then again whenever anything that moves the target or resizes the
  // popover happens — one measurement per frame at most.
  useLayoutEffect(() => {
    measure();

    let frame = 0;
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener('resize', schedule);
    // Capture: a scroll inside any container moves the target as surely as the window's does.
    window.addEventListener('scroll', schedule, true);
    const observer = new ResizeObserver(schedule);
    if (target) observer.observe(target);
    if (panelRef.current) observer.observe(panelRef.current);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      observer.disconnect();
    };
  }, [measure, panelRef, target]);

  const glide = 'motion-safe:transition-[top,left,width,height] motion-safe:duration-200';

  return (
    <div className="fixed inset-0 z-60">
      {/* Takes every click on the page while the tour is up. */}
      <div aria-hidden="true" className="absolute inset-0" />

      {placement?.hole ? (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute rounded-lg shadow-[0_0_0_9999px_rgb(0_0_0/0.55)]',
            'ring-2 ring-white/80',
            glide,
          )}
          style={{
            top: placement.hole.top,
            left: placement.hole.left,
            width: placement.hole.width,
            height: placement.hole.height,
          }}
        />
      ) : (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black/55" />
      )}

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'absolute w-[min(20rem,calc(100vw-2rem))] rounded-lg bg-surface p-4 shadow-raised outline-none',
          // Transparent, not absent, until measured: the size it is measured at is the size it shows
          // at. Not `invisible` either — a hidden control cannot take focus, and focus lands here in
          // the same commit.
          placement ? glide : 'pointer-events-none opacity-0',
        )}
        style={{ top: placement?.top ?? 0, left: placement?.left ?? 0 }}
      >
        {placement && placement.side !== 'center' ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute size-3 rotate-45 bg-surface',
              placement.side === 'bottom' ? '-top-1.5' : '-bottom-1.5',
            )}
            style={{ left: placement.arrowX - 6 }}
          />
        ) : null}

        <h2 id={titleId} className="text-base font-semibold text-ink">
          {title}
        </h2>
        <div className="mt-1.5">{children}</div>
      </div>
    </div>
  );
}
